/**
 * The ledger — every customer's accounting documents.
 *
 * The list leads with what is owed. An overdue invoice is the only row here
 * that is *waiting for somebody to act*, so the filters put it one click away
 * and the badge is drawn as a danger: an ADV opening this page should be able
 * to see, without reading, whether anything is late.
 *
 * The search covers the customer as well as the document number, because the
 * question that arrives by telephone is "what does TRANSLOG owe" at least as
 * often as it is "what about invoice FA-2026-11874".
 *
 * Two actions, two permissions, and they are deliberately not one. Issuing a
 * document and recording a payment are different jobs at a distributor —
 * often different people — and a role that invoices should not be able to
 * declare an invoice paid.
 */

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { useCan } from "@hardauto/core-access";
import type { ApiClient } from "@hardauto/core-api";
import { download, useAction, useDebounced, useResource } from "@hardauto/core-data";
import { day, money } from "@hardauto/core-i18n";
import {
  Badge,
  Button,
  Chips,
  DataTable,
  Input,
  Page,
  Pagination,
  Toolbar,
  useToast,
  type Chip,
  type Column,
} from "@hardauto/core-ui";

import { BillingApi, type DocumentRow } from "../api";
import { IssueDialog } from "../IssueDialog";
import { SettleDialog } from "../SettleDialog";
import { NAMESPACE } from "../i18n";
import { BILLING_PATHS } from "../paths";
import { STATUS_TONE } from "../status";
import "./billing.css";

const PAGE = 50;

/** The filters, as the API expresses them. */
type Filter = "unpaid" | "overdue" | "credits";

export function LedgerScreen({ client }: { client: ApiClient }) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const locale = i18n.language;
  const api = useMemo(() => new BillingApi(client), [client]);
  const action = useAction();
  const toast = useToast();

  const mayIssue = useCan("billing.document.write");
  const maySettle = useCan("billing.settlement.write");

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter | null>(null);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [issuing, setIssuing] = useState(false);
  const [settling, setSettling] = useState<DocumentRow | null>(null);
  const term = useDebounced(search, 300);

  const fetcher = useCallback(
    () =>
      api.list({
        search: term || undefined,
        kind: filter === "credits" ? "credit_note" : undefined,
        unpaid_only: filter === "unpaid" || undefined,
        overdue_only: filter === "overdue" || undefined,
        after_id: cursor,
        limit: PAGE,
      }),
    [api, term, filter, cursor],
  );
  const documents = useResource(fetcher);

  const chips: Chip[] = [
    { id: "unpaid", label: t("chip.unpaid") },
    { id: "overdue", label: t("chip.overdue") },
    { id: "credits", label: t("chip.credits") },
  ];

  const columns: Column<DocumentRow>[] = [
    {
      key: "number",
      header: t("column.number"),
      render: (row) => (
        <div className="hardauto-cell__stack">
          <Link to={BILLING_PATHS.document.replace("$id", row.document.id)}>
            <code className="hardauto-billing__number">{row.document.number}</code>
          </Link>
          <span className="hardauto-cell__muted">{t(`kind.${row.document.kind}`)}</span>
        </div>
      ),
    },
    {
      key: "client",
      header: t("column.client"),
      render: (row) => (
        <div className="hardauto-cell__stack">
          <Link to={BILLING_PATHS.statement.replace("$id", row.client_id)}>{row.client_name}</Link>
          <span className="hardauto-cell__muted">{row.client_code}</span>
        </div>
      ),
    },
    {
      key: "date",
      header: t("column.date"),
      render: (row) => day(row.document.issued_on, locale),
    },
    { key: "due", header: t("column.due"), render: (row) => day(row.document.due_on, locale) },
    {
      key: "total",
      header: t("column.total"),
      align: "end",
      render: (row) => <span className="hardauto-num">{money(row.document.total, locale)}</span>,
    },
    {
      key: "outstanding",
      header: t("column.outstanding"),
      align: "end",
      render: (row) => (
        <span className="hardauto-num hardauto-num--strong">
          {money(row.document.outstanding, locale)}
        </span>
      ),
    },
    {
      key: "status",
      header: t("column.status"),
      render: (row) => (
        <Badge tone={STATUS_TONE[row.document.status]}>{t(`status.${row.document.status}`)}</Badge>
      ),
    },
    {
      key: "actions",
      header: t("column.actions"),
      align: "end",
      render: (row) => (
        <div className="hardauto-billing__row-actions">
          {/* Offered only where it can do something: a paid invoice and a
              credit note have nothing to settle, and a button that opens a
              dialog with no payable document is a dead end. */}
          {maySettle && Number(row.document.outstanding) > 0 ? (
            // Short label, full accessible name. "Enregistrer un règlement"
            // in the cell pushed the two actions onto separate lines and made
            // every row twice as tall; a screen reader still hears which
            // document the payment would go against.
            <Button
              size="sm"
              aria-label={t("settle.openFor", { number: row.document.number })}
              onClick={() => setSettling(row)}
            >
              {t("settle.short")}
            </Button>
          ) : null}
          <Button
            size="sm"
            busy={action.isBusy(`pdf:${row.document.id}`)}
            busyLabel={t("document.pdf.downloading")}
            onClick={() =>
              void action.run(`pdf:${row.document.id}`, async () => {
                try {
                  download(await api.pdf(row.document.id), `${row.document.number}.pdf`);
                } catch {
                  toast.show(t("document.pdf.failed"), { tone: "danger" });
                }
              })
            }
          >
            {t("document.pdf.download")}
          </Button>
        </div>
      ),
    },
  ];

  const filtering = Boolean(term || filter);

  return (
    <Page
      title={t("ledger.title")}
      description={t("ledger.description")}
      eyebrow={documents.data ? t("ledger.count", { count: documents.data.total }) : undefined}
      // No "record a payment" button up here, deliberately: a payment needs a
      // customer, and the page as a whole does not have one. It is offered on
      // the row and on the customer's own situation, where the customer is
      // unambiguous — which is what stops a payment being recorded against
      // whichever company happened to be on screen.
      actions={
        mayIssue ? (
          <Button variant="primary" onClick={() => setIssuing(true)}>
            {t("issue.open")}
          </Button>
        ) : undefined
      }
    >
      <Toolbar>
        <Input
          type="search"
          className="hardauto-search"
          aria-label={t("ledger.searchLabel")}
          placeholder={t("ledger.searchPlaceholder")}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setCursor(undefined);
          }}
        />
        <Chips
          chips={chips}
          active={filter}
          label={t("ledger.filterLabel")}
          allLabel={t("ledger.allDocuments")}
          onSelect={(id) => {
            setFilter(id as Filter | null);
            setCursor(undefined);
          }}
        />
      </Toolbar>

      <DataTable<DocumentRow>
        caption={t("ledger.caption")}
        columns={columns}
        rows={documents.data?.items ?? []}
        rowKey={(row) => row.document.id}
        loading={documents.loading}
        loadingLabel={t("ledger.loading")}
        error={
          documents.failed
            ? {
                title: t("error.title"),
                description: t("error.body"),
                onRetry: documents.reload,
                retryLabel: t("error.retry"),
              }
            : undefined
        }
        empty={
          filtering
            ? { title: t("ledger.filtered.title"), description: t("ledger.filtered.body") }
            : { title: t("ledger.empty.title"), description: t("ledger.empty.body") }
        }
      />

      <Pagination
        nextLabel={t("ledger.next")}
        previousLabel={t("ledger.previous")}
        onNext={
          documents.data?.next_cursor
            ? () => setCursor(documents.data?.next_cursor ?? undefined)
            : undefined
        }
        onPrevious={cursor ? () => setCursor(undefined) : undefined}
        {...(documents.data ? { summary: t("ledger.count", { count: documents.data.total }) } : {})}
        busy={documents.refreshing}
      />

      {mayIssue ? (
        <IssueDialog
          api={api}
          open={issuing}
          onClose={() => setIssuing(false)}
          onIssued={() => {
            setCursor(undefined);
            documents.reload();
          }}
        />
      ) : null}

      {maySettle && settling ? (
        <SettleDialog
          api={api}
          open
          onClose={() => setSettling(null)}
          clientId={settling.client_id}
          clientLabel={`${settling.client_name} (${settling.client_code})`}
          documents={[settling.document]}
          documentId={settling.document.id}
          onRecorded={documents.reload}
        />
      ) : null}
    </Page>
  );
}
